import { useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import UploadIcon from '@mui/icons-material/UploadFile'
import { apiFetch, ApiError } from '../../api/client.js'

/**
 * Import CSV des clients (§1.4 — sans reprise de l'existant, un client ne
 * migre jamais).
 *
 * Deux temps volontairement séparés : on VALIDE d'abord (dryRun), on écrit
 * ensuite. Voir « 412 lignes seront importées » avant de s'engager change
 * complètement la confiance qu'on accorde à l'opération.
 */

interface ImportError {
  line: number
  column?: string
  message: string
}

interface ImportReport {
  imported: number
  ignored?: number
  errors: ImportError[]
  preview?: { line: number; displayName: string }[]
}

/**
 * Modèles CSV, un par entité.
 *
 * ⚠️ Le modèle suit l'ENTITÉ, pas le composant. Le rendre paramétrable avait
 * été oublié lors de la mise en commun de cette boîte : l'import d'articles
 * proposait un modèle de clients, dont il aurait ensuite refusé les colonnes.
 */
const MODELES: Record<'customers' | 'items', { nomFichier: string; contenu: string }> = {
  customers: {
    nomFichier: 'modele-clients.csv',
    contenu: [
      'Type;Nom;Prénom;Société;Date de naissance;Email;Téléphone;Ville;Pays;Délai paiement',
      'Particulier;Dupont;Jean;;12/03/1988;j.dupont@mail.cm;0612345678;Douala;CM;30',
      'Société;;;SARL Béton Armé;;contact@beton.cm;0320112233;Yaoundé;CM;45',
    ].join('\r\n'),
  },
  items: {
    nomFichier: 'modele-articles.csv',
    contenu: [
      "Type;Référence;Code-barres;Nom;Description;Unité;Prix d'achat;Prix de vente;Suivi stock;Seuil d'alerte",
      'Produit;ART-0001;2000000000017;Chargeur USB-C 30 W;Charge rapide;piece;3500;6500;Oui;5',
      'Service;SRV-0001;;Installation à domicile;Déplacement inclus;hour;0;15000;Non;0',
    ].join('\r\n'),
  },
}

/**
 * ⭐ La MÊME boîte sert aux clients et aux articles : la mécanique — choisir un
 * fichier, vérifier à blanc, écrire — est identique, seuls le chemin et le
 * vocabulaire changent. Deux boîtes jumelles auraient divergé à la première
 * retouche, et la protection contre les doublons n'aurait été corrigée que
 * dans l'une des deux.
 */
export function ImportDialog({
  open,
  storeId,
  onClose,
  onImported,
  entite = 'customers',
  titre = 'Importer des clients',
  colonnes = 'Type, Nom, Prénom, Société, Date de naissance, Email, Téléphone, Mobile, Adresse, Code postal, Ville, Pays, Référence, Délai paiement, Notes',
}: {
  open: boolean
  storeId: string
  onClose: () => void
  onImported: () => void
  /** Segment d'URL de l'API : `customers` ou `items`. */
  entite?: 'customers' | 'items'
  titre?: string
  colonnes?: string
}): JSX.Element {
  const [csv, setCsv] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)
  const [report, setReport] = useState<ImportReport | null>(null)
  const [validated, setValidated] = useState(false)
  const [busy, setBusy] = useState(false)
  const [banner, setBanner] = useState<string | null>(null)

  function reset(): void {
    setCsv('')
    setFileName(null)
    setReport(null)
    setValidated(false)
    setBanner(null)
  }

  async function onFile(file: File): Promise<void> {
    reset()
    setFileName(file.name)
    setCsv(await file.text())
  }

  async function run(dryRun: boolean): Promise<void> {
    setBusy(true)
    setBanner(null)
    try {
      const result = await apiFetch<ImportReport>(
        `/stores/${storeId}/${entite}/import${dryRun ? '?dryRun=1' : ''}`,
        {
          method: 'POST',
          body: { csv },
          // ⚠️ SANS l'identifiant d'appareil : l'import écrit côté serveur sans
          // passer par la réplique locale. Attribué à cet appareil, le filtre
          // anti-écho refuserait ensuite de lui renvoyer ces lignes — elles
          // existeraient en base et resteraient invisibles ici, pour toujours.
          sansAppareil: true,
        },
      )
      setReport(result)
      if (dryRun) {
        setValidated(result.errors.length === 0)
      } else {
        onImported()
        onClose()
        reset()
      }
    } catch (err) {
      if (err instanceof ApiError) {
        // 422 : le rapport détaillé est dans le corps, pas dans le message.
        const body = err as ApiError & { errors?: ImportError[] }
        setReport({ imported: 0, ignored: 0, errors: (body.errors as ImportError[]) ?? [] })
        setBanner(err.message)
      } else {
        setBanner('Import impossible. Vérifiez votre connexion.')
      }
      setValidated(false)
    } finally {
      setBusy(false)
    }
  }

  function downloadModele(): void {
    const modele = MODELES[entite]
    // BOM UTF-8 : sans lui, Excel affiche « Référence » comme « RÃ©fÃ©rence ».
    const blob = new Blob(['﻿' + modele.contenu], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = modele.nomFichier
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{titre}</DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
          Fichier CSV, séparateur point-virgule ou virgule. Colonnes reconnues : {colonnes}.{' '}
          <Link component="button" variant="body2" onClick={downloadModele}>
            Télécharger un modèle
          </Link>
        </Typography>

        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadIcon />}
          fullWidth
          sx={{ py: 4, mb: 4 }}
        >
          {fileName ?? 'Choisir un fichier CSV'}
          <input
            hidden
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void onFile(file)
            }}
          />
        </Button>

        {banner && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {banner}
          </Alert>
        )}

        {report && report.errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 4 }}>
            <AlertTitle>
              {report.errors.length} problème{report.errors.length > 1 ? 's' : ''} — rien n’a été
              importé
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              L’import est tout ou rien : corrigez le fichier et relancez.
            </Typography>
            <List dense disablePadding sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {report.errors.slice(0, 20).map((e, i) => (
                <ListItem key={`${e.line}-${i}`} disablePadding>
                  <ListItemText
                    primary={`Ligne ${e.line}${e.column ? ` · ${e.column}` : ''} : ${e.message}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {report && validated && (
          <Alert severity="success">
            <AlertTitle>
              {report.imported} ligne(s) prêtes à importer
              {report.ignored ? ` — ${report.ignored} déjà présente(s), ignorée(s)` : ''}
            </AlertTitle>
            {report.preview && report.preview.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Aperçu :
                </Typography>
                {report.preview.map((p) => (
                  <Typography key={p.line} variant="body2" sx={{ color: 'text.secondary' }}>
                    · {p.displayName}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button disabled={!csv || busy} onClick={() => void run(true)}>
          Vérifier
        </Button>
        <Button
          variant="contained"
          disabled={!validated || busy}
          onClick={() => void run(false)}
        >
          {busy ? 'Import…' : `Importer${report ? ` ${report.imported}` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
